import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { LogOut, Heart, Zap, TrendingUp, BookOpen, History, Sparkles, Target, Wifi, WifiOff, RefreshCw, Swords, Users, Copy, ArrowRight, Trophy, Medal, HandHeart, Shield, ShoppingBag, Coins, FlaskConical, ChevronDown, ChevronUp } from 'lucide-react';
import { studentAPI, powerAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { calculateXPNeeded, getMonsterGif, getMonsterGifByMonster, getMonsterFallbackGif, getClassBadge, getClassName, getEggGif, loadUploadedGifs, subscribeToCacheUpdates, getMonsterDisplayName, refreshGifCache, getMonsterNameByIndex, getGifCache } from '../utils/helpers';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import ThemeToggle from '../components/ThemeToggle';
import MochilaMenu from '../components/MochilaMenu';
import { useWebSocket } from '../hooks/useWebSocket';
import { useVisualFeedback, ScreenFlash, FEEDBACK_TYPES } from '@/components/VisualFeedback';
import confetti from 'canvas-confetti';
import soundManager from '../utils/soundManager';
import PokemonImage from '../components/PokemonImage';
import PokemonEvolutionAnimation from '../components/PokemonEvolutionAnimation';
import CaptureFeed from '../components/CaptureFeed';
import { resolveSpriteUrl } from '../utils/spriteResolver';
import { getPokemonTypes, TYPE_VISUALS } from '../utils/pokemonTypes';
import PokemonTypeCanvas from '../components/PokemonTypeCanvas';
import '../styles/pokemonTypeBackgrounds.css';


// Support Received Overlay (Light Beam + Notification)
const SupportReceivedOverlay = ({ data, onClose }) => {
  const isHP = data.effectType === 'hp';
  return (
    <div
      data-testid="support-received-overlay"
      className="fixed inset-0 z-[90] pointer-events-none flex items-start justify-center"
      style={{ animation: 'supportFadeIn 0.3s ease-out' }}
    >
      {/* Subtle light beam from top */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-64 opacity-40"
        style={{
          height: '100vh',
          background: isHP
            ? 'linear-gradient(180deg, rgba(34,197,94,0.4) 0%, transparent 60%)'
            : 'linear-gradient(180deg, rgba(6,182,212,0.4) 0%, transparent 60%)',
          animation: 'supportBeam 2s ease-out forwards'
        }}
      />
      {/* Notification card */}
      <div
        className="mt-24 relative bg-white/95 backdrop-blur-lg rounded-2xl px-6 py-4 shadow-2xl border-2 pointer-events-auto"
        style={{
          borderColor: isHP ? '#22c55e' : '#06b6d4',
          animation: 'supportSlideDown 0.5s ease-out',
          boxShadow: isHP
            ? '0 0 40px rgba(34,197,94,0.3)'
            : '0 0 40px rgba(6,182,212,0.3)'
        }}
        onClick={onClose}
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isHP ? 'bg-green-100' : 'bg-cyan-100'
          }`} style={{ animation: 'supportPulse 1s ease-in-out infinite' }}>
            {isHP ? (
              <Heart className="w-6 h-6 text-green-600" />
            ) : (
              <Zap className="w-6 h-6 text-cyan-600" />
            )}
          </div>
          <div>
            <p className="text-base font-outfit font-bold text-slate-900">
              {data.fromName} te ajudou!
            </p>
            <p className="text-sm font-nunito text-slate-600">
              {data.powerName}: <span className={`font-bold ${isHP ? 'text-green-600' : 'text-cyan-600'}`}>+{data.effectValue} {isHP ? 'HP' : 'Energia'}</span>
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes supportFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes supportBeam { 0% { opacity: 0; transform: translateX(-50%) scaleY(0); } 30% { opacity: 0.5; transform: translateX(-50%) scaleY(1); } 100% { opacity: 0; } }
        @keyframes supportSlideDown { from { transform: translateY(-30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes supportPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.15); } }
      `}</style>
    </div>
  );
};

// Time ago helper for Mural de Gratidão
const getTimeAgo = (isoString) => {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [eggGif, setEggGif] = useState(null);
  const [gifCacheReady, setGifCacheReady] = useState(false); // Track GIF cache state
  
  // Evolution animation states
  const [showEvolution, setShowEvolution] = useState(false);
  const [evolutionData, setEvolutionData] = useState(null);
  
  // Win Streak states (Mochila Menu handles its own open state internally)
  const [winStreak, setWinStreak] = useState(0);
  const [xpMultiplier, setXpMultiplier] = useState(1.0);
  
  // Visual feedback states
  const { screenFlash, triggerEffect } = useVisualFeedback();
  const [cardEffect, setCardEffect] = useState(null);
  const [glowingBar, setGlowingBar] = useState(null);
  const [isShaking, setIsShaking] = useState(false);
  const [showItemParticles, setShowItemParticles] = useState(false);
  const previousStatsRef = useRef(null);

  // Duel system states
  const [availableDuels, setAvailableDuels] = useState([]);
  const [duelLobbyOpen, setDuelLobbyOpen] = useState(false);
  const [selectedDuel, setSelectedDuel] = useState(null);
  const [lobbyMode, setLobbyMode] = useState(null); // 'create' or 'join'
  const [lobbyCode, setLobbyCode] = useState('');
  const [createdLobby, setCreatedLobby] = useState(null);
  const [joiningLobby, setJoiningLobby] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  // Classroom ranking states
  const [classroomRanking, setClassroomRanking] = useState([]);
  const [currentStudentRankId, setCurrentStudentRankId] = useState(null);
  // Support notification overlay
  const [supportNotification, setSupportNotification] = useState(null);
  // Mural de Gratidão
  const [supportHistory, setSupportHistory] = useState([]);
  const [supportGroupName, setSupportGroupName] = useState(null);
  // Team / Group status
  const [groupStatus, setGroupStatus] = useState(null);
  // Poke-Ciência Widget
  const [pokeCiencia, setPokeCiencia] = useState(null);
  const [pokeCienciaExpanded, setPokeCienciaExpanded] = useState(false);
  const [coinFlash, setCoinFlash] = useState(false);
  const prevCoinsRef = useRef(null);
  // Green Signals (Semáforo de Desempenho)
  const [greenSignals, setGreenSignals] = useState({ green: 0, yellow: 0, red: 0 });

  // Reset coin flash after animation
  useEffect(() => {
    if (coinFlash) {
      const timer = setTimeout(() => setCoinFlash(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [coinFlash]);
  // Refresh all data including GIF cache
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Refresh GIF cache first
      await refreshGifCache();
      setEggGif(getEggGif());
      // Reload all student data
      await loadData();
      // Reload win streak
      const response = await studentAPI.getWinStreak();
      setWinStreak(response.data.win_streak || 0);
      setXpMultiplier(response.data.xp_multiplier || 1.0);
      toast.success('Dados atualizados com sucesso!', { icon: '🔄' });
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Erro ao atualizar dados');
    } finally {
      setRefreshing(false);
    }
  };

  // Listen for global window events from QuickMenu (refresh / logout / open duel).
  useEffect(() => {
    const onRefresh = () => { handleRefresh(); };
    const onLogout  = () => { handleLogout(); };
    const onOpenDuel = () => {
      const d = availableDuels.find(x => x.can_participate);
      if (d) openDuelLobby(d);
      else toast.info('Nenhum duelo disponível agora.');
    };
    window.addEventListener('dashboard-refresh', onRefresh);
    window.addEventListener('dashboard-logout', onLogout);
    window.addEventListener('dashboard-open-duel', onOpenDuel);
    return () => {
      window.removeEventListener('dashboard-refresh', onRefresh);
      window.removeEventListener('dashboard-logout', onLogout);
      window.removeEventListener('dashboard-open-duel', onOpenDuel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDuels]);

  // ============ DUEL SYSTEM FUNCTIONS ============
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Load classroom ranking
  const loadClassroomRanking = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/student/classroom-ranking`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClassroomRanking(data.ranking || []);
        setCurrentStudentRankId(data.current_student_id);
      }
    } catch (error) {
      console.error('Failed to load classroom ranking:', error);
    }
  };

  // Load support history (Mural de Gratidão)
  const loadSupportHistory = async () => {
    try {
      const response = await powerAPI.getSupportHistory();
      setSupportHistory(response.data.history || []);
      setSupportGroupName(response.data.group_name);
    } catch (error) {
      // Silently fail - not critical
    }
  };

  // Load accumulated traffic-light signals (Semáforo de Desempenho)
  const loadGreenSignals = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/student/green-signals-summary`, {
        credentials: 'include'
      });
      if (resp.ok) {
        const data = await resp.json();
        setGreenSignals({
          green: data.green || 0,
          yellow: data.yellow || 0,
          red: data.red || 0
        });
      }
    } catch (error) {
      // Silently fail - non critical
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    loadGreenSignals();
  }, [loadGreenSignals]);

  // Load group status (Team Card)
  const loadGroupStatus = async () => {
    try {
      const response = await powerAPI.getGroupStatus();
      setGroupStatus(response.data);
    } catch (error) {
      // Silently fail
    }
  };

  // Load Poke-Ciência
  const loadPokeCiencia = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/student/poke-ciencia`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.curiosity) {
          setPokeCiencia(data);
        }
      }
    } catch (error) {
      // Silently fail
    }
  }, []);

  // Load available duels
  const loadAvailableDuels = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/available`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableDuels(data);
      }
    } catch (error) {
      console.error('Failed to load available duels:', error);
    }
  };

  // Create duel lobby
  const handleCreateLobby = async (duel) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/${duel.duel_id}/create-lobby`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create lobby');
      }
      
      const data = await response.json();
      setCreatedLobby(data);
      setLobbyCode(data.lobby_code);
      setLobbyMode('create');
      toast.success('Sala criada! Compartilhe o código com seu amigo.', { icon: '⚔️' });
    } catch (error) {
      console.error('Failed to create lobby:', error);
      toast.error(error.message || 'Erro ao criar sala');
    }
  };

  // Join duel lobby
  const handleJoinLobby = async () => {
    if (joinCode.length !== 6) {
      toast.error('Digite um código de 6 dígitos');
      return;
    }
    
    setJoiningLobby(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/join-lobby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lobby_code: joinCode })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to join lobby');
      }
      
      const data = await response.json();
      toast.success('Entrou na sala! Aguardando início...', { icon: '⚔️' });
      
      // Navigate to duel arena
      navigate(`/student/duel/${data.lobby_id}`);
    } catch (error) {
      console.error('Failed to join lobby:', error);
      toast.error(error.message || 'Erro ao entrar na sala');
    } finally {
      setJoiningLobby(false);
    }
  };

  // Start duel match (host only)
  const handleStartDuel = async () => {
    if (!createdLobby) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/duels/lobby/${createdLobby.lobby_id}/start`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start duel');
      }
      
      const data = await response.json();
      // Navigate to duel arena with match data
      navigate(`/student/duel/${createdLobby.lobby_id}`, { state: { matchData: data } });
    } catch (error) {
      console.error('Failed to start duel:', error);
      toast.error(error.message || 'Erro ao iniciar duelo');
    }
  };

  // Copy lobby code to clipboard
  const copyLobbyCode = () => {
    navigator.clipboard.writeText(lobbyCode);
    toast.success('Código copiado!', { icon: '📋' });
  };

  // Open duel lobby modal
  const openDuelLobby = (duel) => {
    if (duel.already_participated) {
      toast.error('Voce ja participou deste desafio! Aguarde o proximo duelo do Mestre.');
      return;
    }
    setSelectedDuel(duel);
    setDuelLobbyOpen(true);
    setLobbyMode(null);
    setCreatedLobby(null);
    setJoinCode('');
  };
  
  // Load win streak data
  useEffect(() => {
    const loadWinStreak = async () => {
      try {
        const response = await studentAPI.getWinStreak();
        setWinStreak(response.data.win_streak || 0);
        setXpMultiplier(response.data.xp_multiplier || 1.0);
      } catch (error) {
        console.error('Failed to load win streak:', error);
      }
    };
    loadWinStreak();
  }, []);

  // Handler para mensagens WebSocket
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[StudentDashboard] WebSocket message:', message);
    
    // Handle new lab material notification
    if (message.type === 'lab_new_material') {
      toast.info(message.message || 'Novo material no Laboratório!', { duration: 5000, description: 'Clique no botão Lab para estudar' });
      return;
    }
    
    // Handle pokemon change
    if (message.type === 'pokemon_changed') {
      const data = message.data;
      
      // Update the monster in local state
      setStudents(prev => prev.map(student => {
        if (student.monster) {
          return {
            ...student,
            monster: data.monster
          };
        }
        return student;
      }));
      
      // Show notification to user
      toast.success(`🔄 Seu Pokémon foi alterado: ${data.old_name} → ${data.new_name}`, { duration: 5000 });
      
      // Reload student data to get fresh monster info
      loadData();
      return;
    }
    
    if (message.type === 'stats_update') {
      const data = message.data;
      
      // Get previous stats for comparison
      const prevStats = previousStatsRef.current;
      
      // Determine what changed and trigger visual effects
      if (prevStats) {
        // HP changed
        if (data.hp !== undefined && data.hp !== prevStats.hp) {
          if (data.hp < prevStats.hp) {
            // Damage taken - red flash + shake + sound
            triggerEffect(FEEDBACK_TYPES.DAMAGE);
            setCardEffect('card-damage-flash');
            setIsShaking(true);
            setGlowingBar('hp-damage');
            soundManager.playHPDamage();
            setTimeout(() => {
              setIsShaking(false);
              setCardEffect(null);
              setGlowingBar(null);
            }, 600);
          } else {
            // Healed - green glow + sound
            triggerEffect(FEEDBACK_TYPES.HEAL);
            setCardEffect('card-heal-flash');
            setGlowingBar('hp-heal');
            soundManager.playHPHeal();
            setTimeout(() => {
              setCardEffect(null);
              setGlowingBar(null);
            }, 700);
          }
        }
        
        // Energy changed
        if (data.energy !== undefined && data.energy !== prevStats.energy) {
          if (data.energy > prevStats.energy) {
            // Energy recharged - cyan glow + power-up sound
            triggerEffect(FEEDBACK_TYPES.ENERGY);
            setCardEffect('card-energy-flash');
            setGlowingBar('energy');
            soundManager.playEnergyRecharge();
            setTimeout(() => {
              setCardEffect(null);
              setGlowingBar(null);
            }, 600);
          }
        }
        
        // XP changed
        if (data.xp !== undefined && data.xp !== prevStats.xp) {
          if (data.xp > prevStats.xp || (data.level !== undefined && data.level > prevStats.level)) {
            // XP gained - blue glow + sound
            triggerEffect(FEEDBACK_TYPES.XP_GAIN);
            setCardEffect('card-xp-flash');
            setGlowingBar('xp');
            soundManager.playXPGain();
            setTimeout(() => {
              setCardEffect(null);
              setGlowingBar(null);
            }, 900);
          } else {
            // XP lost - sound
            triggerEffect(FEEDBACK_TYPES.XP_LOSS);
            setGlowingBar('xp-loss');
            soundManager.playXPLoss();
            setTimeout(() => setGlowingBar(null), 500);
          }
        }
        
        // Level up!
        if (data.level !== undefined && data.level > prevStats.level) {
          triggerEffect(FEEDBACK_TYPES.LEVEL_UP);
          soundManager.playLevelUp();
          // Refresh Poke-Ciência when level changes
          loadPokeCiencia();
        }
      }
      
      // Atualiza o estado local com os novos valores
      setStudents(prev => prev.map(student => {
        const updates = {};
        
        if (data.hp !== undefined) updates.hp = data.hp;
        if (data.max_hp !== undefined) updates.max_hp = data.max_hp;
        if (data.energy !== undefined) updates.energy = data.energy;
        if (data.max_energy !== undefined) updates.max_energy = data.max_energy;
        if (data.xp !== undefined) updates.xp = data.xp;
        if (data.level !== undefined) updates.level = data.level;
        
        // Update monster stage AND name if evolution occurred
        if (data.evolution && student.monster) {
          updates.monster = {
            ...student.monster,
            stage: data.evolution.to_stage,
            name: data.evolution.monster_name // Update the monster name too
          };
        }
        
        // Store current stats for next comparison
        previousStatsRef.current = {
          hp: updates.hp ?? student.hp,
          energy: updates.energy ?? student.energy,
          xp: updates.xp ?? student.xp,
          level: updates.level ?? student.level
        };
        
        return Object.keys(updates).length > 0 
          ? { ...student, ...updates }
          : student;
      }));
      
      // Check for evolution and show animation
      if (data.evolution && data.evolution.evolved) {
        // Trigger evolution effect and sound
        triggerEffect(FEEDBACK_TYPES.EVOLUTION);
        soundManager.playEvolution();
        
        // Trigger evolution animation
        setEvolutionData({
          monsterName: data.evolution.monster_name,
          fromStage: data.evolution.from_stage,
          toStage: data.evolution.to_stage,
          fromName: data.evolution.from_name || data.evolution.previous_name,
          toName: data.evolution.to_name || data.evolution.monster_name,
        });
        setShowEvolution(true);
        
        // Refresh Poke-Ciência when Pokémon evolves
        loadPokeCiencia();
        
        // Fire confetti for evolution
        setTimeout(() => {
          confetti({
            particleCount: 200,
            spread: 100,
            origin: { y: 0.5 },
            colors: ['#FFD700', '#FFA500', '#FF6347', '#9333EA']
          });
        }, 500);
        
        return; // Skip other toasts during evolution
      }
      
      // Trigger particle effect when item is used
      if (data.trigger_particles) {
        setShowItemParticles(true);
        setTimeout(() => setShowItemParticles(false), 1500);
      }
      
      // Mostra feedback visual baseado no tipo de mudança
      if (data.change_type === 'hp_healed') {
        toast.success(`+${data.change_amount} HP 💚`, { duration: 3000 });
      } else if (data.change_type === 'hp_lost') {
        toast.error(`${data.change_amount} HP 💔`, { duration: 3000 });
      } else if (data.change_type === 'energy_recharged') {
        toast.success(`+${data.change_amount} Energia ⚡`, { duration: 3000 });
      } else if (data.change_type === 'xp_adjusted') {
        if (data.change_amount > 0) {
          toast.success(`+${data.change_amount} AURA ⭐`, { duration: 3000 });
        } else {
          toast.error(`${data.change_amount} AURA 📉`, { duration: 3000 });
        }
        
        // Se subiu de nível, mostra animação
        if (data.level_up) {
          setTimeout(() => {
            confetti({
              particleCount: 150,
              spread: 100,
              origin: { y: 0.6 }
            });
            toast.success(`🎉 Você subiu para o nível ${data.level}!`, { duration: 5000 });
            
            if (data.unlocked_powers && data.unlocked_powers.length > 0) {
              setTimeout(() => {
                toast.success(`✨ Novo poder desbloqueado: ${data.unlocked_powers.join(', ')}`, { duration: 5000 });
              }, 1500);
            }
          }, 500);
        }
      }
    }
    
    // Handle cooperative support received
    if (message.type === 'support_received') {
      const data = message.data;
      // Show "light beam" notification
      setSupportNotification({
        fromName: data.from_name,
        powerName: data.power_name,
        effectType: data.effect_type,
        effectValue: data.effect_value,
        message: data.message
      });
      // Auto-hide after 4 seconds
      setTimeout(() => setSupportNotification(null), 4000);
      // Refresh support history
      loadSupportHistory();
      // Refresh group status (allies' HP/energy changed)
      loadGroupStatus();
      // Also toast
      const emoji = data.effect_type === 'hp' ? '💚' : '⚡';
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-xl">{emoji}</span>
          <div>
            <div className="font-bold">{data.from_name} te ajudou!</div>
            <div className="text-sm opacity-90">{data.power_name}: +{data.effect_value} {data.effect_type === 'hp' ? 'HP' : 'Energia'}</div>
          </div>
        </div>,
        { duration: 5000 }
      );
      return;
    }
    
    // Handle ranking change notifications
    if (message.type === 'ranking_change') {
      const data = message.data;
      
      if (data.type === 'overtook') {
        // This student overtook someone
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#FFD700', '#FFA500', '#FF6347']
        });
        toast.success(
          <div className="flex items-center gap-2">
            <span className="text-2xl">🏆</span>
            <div>
              <div className="font-bold">Você subiu no ranking!</div>
              <div className="text-sm opacity-90">Ultrapassou {data.overtook_student} • Agora é #{data.new_rank}</div>
            </div>
          </div>,
          { duration: 6000 }
        );
      } else if (data.type === 'overtaken') {
        // This student was overtaken
        toast(
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚡</span>
            <div>
              <div className="font-bold">Alguém te ultrapassou!</div>
              <div className="text-sm opacity-90">{data.by_student} passou você • Agora é #{data.new_rank}</div>
            </div>
          </div>,
          { 
            duration: 5000,
            icon: null
          }
        );
      }
    }
  }, []);

  // Handler when evolution animation completes
  const handleEvolutionComplete = useCallback(() => {
    setShowEvolution(false);
    setEvolutionData(null);
    toast.success(`🌟 Evolução completa!`, { duration: 3000 });
  }, []);

  // Conecta ao WebSocket quando temos um student_id
  const studentId = students[0]?.student_id;
  const { isConnected } = useWebSocket(
    'student',
    studentId,
    handleWebSocketMessage
  );

  useEffect(() => {
    loadData();
    loadAvailableDuels();
    loadClassroomRanking();
    loadSupportHistory();
    loadGroupStatus();
    loadPokeCiencia();
    // Poll group status every 10s for real-time HP/energy/online updates
    const groupPollInterval = setInterval(loadGroupStatus, 10000);
    // Load GIF cache first, then set egg GIF
    loadUploadedGifs().then(() => {
      setEggGif(getEggGif());
      setGifCacheReady(true);
    });
    // Subscribe to GIF cache updates for reactive re-renders
    const unsubscribe = subscribeToCacheUpdates(() => {
      setGifCacheReady(true);
      setEggGif(getEggGif());
    });
    return () => {
      unsubscribe();
      clearInterval(groupPollInterval);
    };
  }, []);

  const loadData = async (isRetry = false) => {
    try {
      setLoadError(false);
      const response = await studentAPI.getMe();
      const studentsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.students || []);
      setStudents(studentsData);
      
      if (studentsData.length > 0) {
        const student = studentsData[0];
        previousStatsRef.current = {
          hp: student.hp,
          energy: student.energy,
          xp: student.xp,
          level: student.level
        };

        // Detect retroactive / cross-session evolution. If the backend
        // wrote a `pending_evolution_notice` on the monster (because the
        // sweep evolved the student or they leveled up while offline),
        // fire the evolution animation now and clear the flag.
        const notice = student.monster?.pending_evolution_notice;
        if (notice && !showEvolution) {
          setEvolutionData({
            monsterName: notice.to_name || student.monster?.name,
            fromStage: notice.from_stage,
            toStage: notice.to_stage,
            fromName: notice.from_name,
            toName: notice.to_name,
          });
          setShowEvolution(true);
          // Clear server-side so it doesn't fire again on next reload
          studentAPI.dismissEvolutionNotice?.().catch(() => { /* best-effort */ });
        }
      }
      
      const unhatched = studentsData.find(s => !s.egg_hatched);
      if (unhatched) {
        navigate('/student/egg');
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      if (!isRetry) {
        // Auto-retry once after 2s
        await new Promise(r => setTimeout(r, 2000));
        return loadData(true);
      }
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <nav className="theme-navbar border-b" style={{ backgroundColor: 'var(--navbar-bg)', borderColor: 'var(--navbar-border)' }}>
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-outfit font-black text-primary" data-testid="student-nav-title">BelottoPokeClass Center</h1>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button onClick={handleLogout} variant="ghost" className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                Sair
              </Button>
            </div>
          </div>
        </nav>
        <div className="container mx-auto px-4 py-20 text-center">
          <RefreshCw className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Erro de conexão</h2>
          <p className="font-nunito mb-8" style={{ color: 'var(--text-secondary)' }}>Não foi possível carregar seus dados. Tente novamente.</p>
          <Button
            data-testid="retry-load-btn"
            onClick={() => { setLoading(true); setLoadError(false); loadData(); }}
            className="bg-primary hover:bg-primary/90 text-white h-12 px-8 rounded-xl font-bold shadow-lg"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <nav className="theme-navbar border-b" style={{ backgroundColor: 'var(--navbar-bg)', borderColor: 'var(--navbar-border)' }}>
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-outfit font-black text-primary" data-testid="student-nav-title">BelottoPokeClass Center</h1>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button onClick={handleLogout} variant="ghost" className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                Sair
              </Button>
            </div>
          </div>
        </nav>
        <div className="container mx-auto px-4 py-20 text-center">
          <BookOpen className="w-20 h-20 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-outfit font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Nenhuma Sala de Aula</h2>
          <p className="font-nunito mb-8" style={{ color: 'var(--text-secondary)' }}>Entre em uma sala usando o código fornecido pelo professor</p>
          <Button
            data-testid="join-classroom-btn"
            onClick={() => navigate('/student/join')}
            className="bg-primary hover:bg-primary/90 text-white h-12 px-8 rounded-xl font-bold shadow-lg"
          >
            Entrar em uma Sala
          </Button>
        </div>
      </div>
    );
  }

  const currentStudent = students[0];
  const monster = currentStudent.monster;

  // Detect coin changes for flash animation
  const currentCoins = currentStudent.coins || 0;
  if (prevCoinsRef.current !== null && prevCoinsRef.current !== currentCoins) {
    if (!coinFlash) setCoinFlash(true);
  }
  prevCoinsRef.current = currentCoins;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Screen Flash Overlay for visual feedback */}
      <ScreenFlash type={screenFlash} />
      
      {/* Evolution Animation Overlay (rich, with sprites + light burst) */}
      {showEvolution && evolutionData && (
        <PokemonEvolutionAnimation
          fromSpriteUrl={
            evolutionData.fromName
              ? resolveSpriteUrl(evolutionData.fromName, getGifCache())
              : null
          }
          fromName={evolutionData.fromName || evolutionData.monsterName}
          toSpriteUrl={
            evolutionData.toName
              ? resolveSpriteUrl(evolutionData.toName, getGifCache())
              : null
          }
          toName={evolutionData.toName || evolutionData.monsterName}
          onDone={handleEvolutionComplete}
        />
      )}

      {/* Support Received Overlay */}
      {supportNotification && (
        <SupportReceivedOverlay data={supportNotification} onClose={() => setSupportNotification(null)} />
      )}
      
      {/* Single-title navbar — every action now lives in the QuickMenu (top-left map header). */}
      <nav className="theme-navbar border-b" style={{ backgroundColor: 'var(--navbar-bg)', borderColor: 'var(--navbar-border)' }}>
        <div className="container mx-auto px-4 py-3 flex justify-center items-center">
          <h1
            className="text-2xl sm:text-3xl font-outfit font-black text-primary tracking-wide"
            data-testid="student-nav-title"
          >
            BelottoPokeClass Center
          </h1>
        </div>
      </nav>

      {/* Hidden utility surface — keeps MochilaMenu mounted (websockets/coin flow)
          but invisible. NotificationBell lives in the QuickMenu. */}
      <div className="hidden">
        <MochilaMenu
          winStreak={winStreak}
          xpMultiplier={xpMultiplier}
          inventoryOnly={true}
          onItemUsed={(data) => {
            if (data.updated_student) {
              setStudents(prev => prev.map(s =>
                s.student_id === data.updated_student.student_id
                  ? { ...s, ...data.updated_student }
                  : s
              ));
            }
            if (data.optimistic && data.coins_earned) {
              setStudents(prev => prev.map(s => ({ ...s, coins: (s.coins || 0) + data.coins_earned })));
            }
            if (data.new_coins !== undefined && !data.optimistic) {
              setStudents(prev => prev.map(s => ({ ...s, coins: data.new_coins })));
            }
            if (data.rollback) {
              setStudents(prev => prev.map(s => ({ ...s, coins: Math.max(0, (s.coins || 0) - 2) })));
            }
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          {/* Monster Card - Hero */}
          <div className="md:col-span-5 lg:col-span-6">
            {(() => {
              const monsterTypes = getPokemonTypes(monster);
              return (
            <div className="bg-white rounded-3xl border-2 border-slate-100 p-8 shadow-sm monster-card h-full" data-testid="monster-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-outfit font-black text-slate-900 mb-2">{monster?.name || 'Sem Monstro'}</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-3 py-1 rounded-md text-xs font-bold uppercase border ${getClassBadge(monster?.class_type || currentStudent.assigned_class)}`}>
                      {getClassName(monster?.class_type || currentStudent.assigned_class)}
                    </span>
                    {monsterTypes.map((t) => {
                      const v = TYPE_VISUALS[t];
                      if (!v) return null;
                      return (
                        <span
                          key={`type-tag-${t}`}
                          data-testid={`pokemon-type-${t}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-outfit font-black uppercase tracking-wider shadow-sm"
                          style={{ backgroundColor: v.color, color: v.textColor, boxShadow: `0 2px 6px ${v.color}55` }}
                          title={`Tipo ${v.label}`}
                        >
                          {v.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-black tracking-wider">
                  LVL {currentStudent.level}
                </div>
              </div>

              <div
                className="type-bg rounded-2xl flex justify-center mb-8 relative py-4"
                data-testid="monster-avatar-box"
              >
                {/* Canvas-based animated backdrop — real particle simulation
                    running at 60fps via requestAnimationFrame. Dual-type auto
                    splits diagonally inside the component. */}
                <PokemonTypeCanvas types={monsterTypes} />

                <div className="type-bg__content flex justify-center w-full relative z-10">
                {/* Item Use Particle Effect */}
                {showItemParticles && (
                  <div className="absolute inset-0 pointer-events-none z-10 overflow-visible">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-3 h-3 rounded-full"
                        style={{
                          left: '50%',
                          top: '50%',
                          background: `radial-gradient(circle, ${
                            ['#FFD700', '#FF69B4', '#00FFFF', '#7CFC00', '#FF6347'][i % 5]
                          } 0%, transparent 70%)`,
                          animation: `particle-burst 1.2s ease-out forwards`,
                          animationDelay: `${i * 0.05}s`,
                          transform: `rotate(${i * 18}deg) translateY(-${60 + (i % 5) * 10}px)`,
                          opacity: 0
                        }}
                      />
                    ))}
                    <style>{`
                      @keyframes particle-burst {
                        0% {
                          opacity: 1;
                          transform: rotate(var(--rotation)) translateY(0) scale(0.5);
                        }
                        50% {
                          opacity: 1;
                          transform: rotate(var(--rotation)) translateY(-80px) scale(1.2);
                        }
                        100% {
                          opacity: 0;
                          transform: rotate(var(--rotation)) translateY(-120px) scale(0);
                        }
                      }
                    `}</style>
                  </div>
                )}
                
                {monster ? (
                  <PokemonImage
                    src={getMonsterGifByMonster(monster)}
                    fallbackSrc={getMonsterFallbackGif(monster)}
                    alt={getMonsterDisplayName(monster)}
                    className={`w-64 h-64 object-contain drop-shadow-2xl ${showItemParticles ? 'animate-pulse' : ''}`}
                    fallbackSize={256}
                  />
                ) : (
                  <div className="w-64 h-64 flex items-center justify-center">
                    {eggGif ? (
                      <img src={eggGif} alt="Ovo" className="w-full h-full object-contain drop-shadow-2xl" />
                    ) : (
                      <span className="text-8xl">🥚</span>
                    )}
                  </div>
                )}
                </div>
              </div>

              <div className="space-y-4">
                {/* HP Bar - Animação CSS independente */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-red-500" />
                      <span className="font-nunito font-bold text-slate-700">HP</span>
                    </div>
                    <span className="font-nunito font-bold text-slate-900">{currentStudent.hp}/{currentStudent.max_hp}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      data-testid="hp-bar"
                      className={`bg-gradient-to-r from-red-500 to-pink-600 h-3 rounded-full
                        ${glowingBar === 'hp-heal' ? 'stat-bar-hp-heal stat-bar-pulse-green' : ''}
                        ${glowingBar === 'hp-damage' ? 'stat-bar-hp-damage' : ''}
                      `}
                      style={{ 
                        width: `${Math.min(100, (currentStudent.hp / currentStudent.max_hp) * 100)}%`,
                        transition: 'width 500ms ease-in-out'
                      }}
                    />
                  </div>
                </div>

                {/* Energy Bar - Animação CSS independente */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-cyan-500" />
                      <span className="font-nunito font-bold text-slate-700">Energia</span>
                    </div>
                    <span className="font-nunito font-bold text-slate-900">{currentStudent.energy}/{currentStudent.max_energy}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      data-testid="energy-bar"
                      className={`bg-gradient-to-r from-cyan-400 to-blue-500 h-3 rounded-full
                        ${glowingBar === 'energy' ? 'stat-bar-energy stat-bar-pulse-cyan' : ''}
                      `}
                      style={{ 
                        width: `${Math.min(100, (currentStudent.energy / currentStudent.max_energy) * 100)}%`,
                        transition: 'width 500ms ease-in-out'
                      }}
                    />
                  </div>
                </div>

                {/* AURA Bar - Animação CSS independente */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-secondary" />
                      <span className="font-nunito font-bold text-slate-700">AURA</span>
                    </div>
                    <span className="font-nunito font-bold text-slate-900">{currentStudent.xp}/{calculateXPNeeded(currentStudent.level)}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                      data-testid="xp-bar"
                      className={`bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full aura-shimmer
                        ${glowingBar === 'xp' ? 'stat-bar-xp stat-bar-pulse-blue' : ''}
                        ${glowingBar === 'xp-loss' ? 'stat-bar-xp-loss' : ''}
                      `}
                      style={{ 
                        width: `${Math.min(100, (currentStudent.xp / calculateXPNeeded(currentStudent.level)) * 100)}%`,
                        transition: 'width 500ms ease-in-out'
                      }}
                    />
                  </div>
                </div>

                {/* Coins Balance */}
                <div
                  className={`flex items-center justify-between rounded-xl px-4 py-2.5 border transition-all duration-500 ${
                    coinFlash
                      ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-300 shadow-lg shadow-emerald-200/50'
                      : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
                  }`}
                  onAnimationEnd={() => setCoinFlash(false)}
                >
                  <div className="flex items-center gap-2">
                    <Coins className={`w-5 h-5 transition-colors duration-300 ${coinFlash ? 'text-emerald-500' : 'text-amber-500'}`} />
                    <span className="font-nunito font-bold text-slate-700">Coins</span>
                  </div>
                  <div className="relative">
                    <span
                      data-testid="coin-balance"
                      className={`font-outfit font-black text-xl transition-all duration-300 ${
                        coinFlash ? 'text-emerald-600 scale-110' : 'text-amber-700'
                      }`}
                      style={{ display: 'inline-block', transform: coinFlash ? 'scale(1.15)' : 'scale(1)' }}
                    >
                      {currentCoins}
                    </span>
                    {coinFlash && (
                      <span
                        className="absolute -top-4 right-0 text-xs font-bold text-emerald-500 pointer-events-none"
                        style={{ animation: 'coinFloat 1s ease-out forwards' }}
                      >
                        +2
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
              );
            })()}
          </div>

          {/* Right Column: Classroom + Estágio + Poke-Ciência */}
          <div className="md:col-span-3 lg:col-span-6 space-y-6">
            {/* Classroom Info */}
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm" data-testid="classroom-info">
              <h3 className="text-lg font-outfit font-bold text-slate-900 mb-4">Sala de Aula</h3>
              <p className="text-2xl font-outfit font-black text-primary mb-2">{currentStudent.classroom.name}</p>
              {currentStudent.assigned_class && (
                <span className={`px-3 py-1 rounded-md text-xs font-bold uppercase border ${getClassBadge(currentStudent.assigned_class)}`}>
                  {getClassName(currentStudent.assigned_class)}
                </span>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 shadow-lg text-white">
              <h3 className="text-sm font-nunito font-bold opacity-90 mb-2">Estágio Atual</h3>
              <p className="text-4xl font-outfit font-black">{monster?.stage || 1}/3</p>
              <p className="text-sm font-nunito opacity-90 mt-2">
                {currentStudent.level < 16 ? 'Evolui no nível 16' : currentStudent.level < 32 ? 'Evolui no nível 32' : 'Máximo!'}
              </p>
            </div>

            {/* Mural de Capturas — capturas recentes dos colegas da turma */}
            <CaptureFeed endpoint="/student/journey-feed" compact />

            {/* Poke-Ciência Widget - Right below Estágio Atual */}
            {pokeCiencia && (
              <div data-testid="poke-ciencia-widget">
                <div
                  className="rounded-2xl border-2 border-slate-100 shadow-sm overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md"
                  style={{ background: 'linear-gradient(135deg, rgba(236,253,245,0.8), rgba(219,234,254,0.6))' }}
                  onClick={() => setPokeCienciaExpanded(prev => !prev)}
                >
                  <div className="p-5">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <FlaskConical className="w-4.5 h-4.5 text-emerald-600" />
                        </div>
                        <h3 className="text-sm font-outfit font-bold text-slate-800">Poke-Ciência</h3>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-nunito tracking-wide">
                          {pokeCiencia.theme === 'terra' ? 'Terra' : pokeCiencia.theme === 'materia' ? 'Matéria' : 'Vida'}
                        </span>
                        {pokeCienciaExpanded
                          ? <ChevronUp className="w-4 h-4 text-slate-400" />
                          : <ChevronDown className="w-4 h-4 text-slate-400" />
                        }
                      </div>
                    </div>

                    {/* Curiosity Text */}
                    <p className="text-sm text-slate-700 font-nunito leading-relaxed" data-testid="poke-ciencia-text">
                      {pokeCiencia.curiosity}
                    </p>

                    {/* Expanded Detail */}
                    {pokeCienciaExpanded && (
                      <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
                        <div className="bg-white/70 rounded-xl p-4 border border-slate-200/60">
                          <p className="text-sm text-slate-600 font-nunito leading-relaxed" data-testid="poke-ciencia-detail">
                            {pokeCiencia.detail}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Win Streak Card - Only show if streak active */}
            {winStreak >= 2 && (
              <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 shadow-lg text-white relative overflow-hidden">
                <div className="absolute top-2 right-2 text-4xl animate-bounce">🔥</div>
                <h3 className="text-sm font-nunito font-bold opacity-90 mb-2">Combo Ativo!</h3>
                <p className="text-4xl font-outfit font-black">x{winStreak}</p>
                <p className="text-sm font-nunito opacity-90 mt-2">
                  +{Math.round((xpMultiplier - 1) * 100)}% de bônus de XP
                </p>
                <div className="flex gap-1 mt-3">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div 
                      key={level}
                      className={`w-4 h-4 rounded-full transition-all duration-300 ${
                        winStreak >= level 
                          ? 'bg-yellow-300 shadow-lg shadow-yellow-500/50' 
                          : 'bg-white/30'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Card - Group Status */}
      <div className="container mx-auto px-4 pb-8" data-testid="team-card-section">
        {groupStatus?.has_group ? (
          <div
            className="rounded-2xl p-6 shadow-lg border-2 overflow-hidden relative"
            style={{
              background: `linear-gradient(135deg, ${groupStatus.group.color}10, ${groupStatus.group.color}05)`,
              borderColor: `${groupStatus.group.color}40`
            }}
            data-testid="team-card"
          >
            {/* Decorative accent */}
            <div className="absolute top-0 left-0 w-full h-1.5 rounded-t-2xl" style={{ backgroundColor: groupStatus.group.color }} />

            <div className="flex items-center gap-3 mb-5 mt-1">
              <div className="p-2.5 rounded-xl" style={{ backgroundColor: groupStatus.group.color }}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-xl text-slate-800" data-testid="team-name">{groupStatus.group.name}</h3>
                <p className="text-sm text-slate-500">Sua equipe &middot; {groupStatus.members.length} aliado{groupStatus.members.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Party List */}
            <div className="space-y-2.5">
              {groupStatus.members.map((member) => {
                const hpPct = Math.min(100, (member.hp / member.max_hp) * 100);
                const energyPct = Math.min(100, (member.energy / member.max_energy) * 100);
                const hpColor = hpPct > 50 ? 'from-green-400 to-emerald-500' : hpPct > 25 ? 'from-yellow-400 to-orange-500' : 'from-red-500 to-rose-600';

                return (
                  <div
                    key={member.student_id}
                    data-testid={`team-member-${member.student_id}`}
                    className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-slate-200/60 hover:border-slate-300 transition-all"
                  >
                    {/* Online indicator + Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden border border-slate-200">
                        {member.monster_index !== null && member.class_type ? (
                          <PokemonImage
                            src={getMonsterGifByMonster({...member, stage: member.monster_stage || 1})}
                            fallbackSrc={getMonsterFallbackGif({...member, stage: member.monster_stage || 1})}
                            alt={member.monster_name || ''}
                            className="w-9 h-9 object-contain"
                            fallbackSize={36}
                          />
                        ) : (
                          <span className="text-lg">?</span>
                        )}
                      </div>
                      {/* Online dot */}
                      <div
                        data-testid={`online-status-${member.student_id}`}
                        className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
                          member.is_online ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                        title={member.is_online ? 'Online' : 'Offline'}
                      />
                    </div>

                    {/* Name + Class */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-nunito font-bold text-sm text-slate-800 truncate">{member.name}</p>
                        {member.class_type && (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${getClassBadge(member.class_type)}`}>
                            {getClassName(member.class_type)}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 font-bold">Nv.{member.level}</span>
                      </div>

                      {/* Mini HP + Energy bars */}
                      <div className="flex items-center gap-3 mt-1.5">
                        <div className="flex items-center gap-1.5 flex-1">
                          <Heart className="w-3 h-3 text-rose-400 flex-shrink-0" />
                          <div className="flex-1 bg-slate-200/80 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${hpColor} transition-all duration-700`}
                              style={{ width: `${hpPct}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-bold text-slate-500 w-12 text-right tabular-nums">{member.hp}/{member.max_hp}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-1">
                          <Zap className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                          <div className="flex-1 bg-slate-200/80 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-700"
                              style={{ width: `${energyPct}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-bold text-slate-500 w-12 text-right tabular-nums">{member.energy}/{member.max_energy}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white/60 dark:bg-slate-800/80 rounded-2xl p-8 border-2 border-dashed border-slate-200 dark:border-slate-600 text-center" data-testid="no-team-card">
            <Users className="w-12 h-12 text-slate-300 dark:text-slate-500 mx-auto mb-3" />
            <p className="font-outfit font-bold text-lg text-slate-400 dark:text-slate-200">Voce ainda nao possui uma equipe.</p>
            <p className="text-sm text-slate-400 dark:text-slate-300 mt-1">Aguarde as ordens do seu Mestre!</p>
          </div>
        )}
      </div>

      {/* Classroom Ranking Card */}
      {classroomRanking.length > 0 && (
        <div className="container mx-auto px-4 pb-8">
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-slate-800 dark:to-slate-700 rounded-2xl p-6 shadow-lg border-2 border-amber-200 dark:border-amber-600">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-500 rounded-xl">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-xl text-slate-800 dark:text-white">Ranking da Turma</h3>
                <p className="text-sm text-slate-500 dark:text-slate-300">Top 10 por nível e AURA</p>
              </div>
            </div>
            
            <div className="space-y-2">
              {classroomRanking.map((student, index) => {
                const isCurrentStudent = student.student_id === currentStudentRankId;
                const getMedalIcon = (rank) => {
                  if (rank === 1) return <Medal className="w-5 h-5 text-yellow-500" />;
                  if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" />;
                  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
                  return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-slate-500">{rank}</span>;
                };
                
                return (
                  <div 
                    key={student.student_id}
                    data-testid={`ranking-position-${student.rank}`}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isCurrentStudent 
                        ? 'bg-gradient-to-r from-amber-200 to-yellow-200 dark:from-amber-600 dark:to-yellow-600 border-2 border-amber-400 shadow-md' 
                        : 'bg-white/60 dark:bg-slate-600/60 hover:bg-white dark:hover:bg-slate-600'
                    }`}
                  >
                    <div className="w-8 flex justify-center">
                      {getMedalIcon(student.rank)}
                    </div>
                    
                    {/* Monster mini icon */}
                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-500 overflow-hidden flex items-center justify-center">
                      {student.monster ? (
                        <img 
                          src={getMonsterGifByMonster(student.monster)} 
                          alt="" 
                          loading="lazy"
                          className="w-8 h-8 object-contain"
                          onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = getMonsterFallbackGif(student.monster); } }}
                        />
                      ) : (
                        <span className="text-lg">🥚</span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${isCurrentStudent ? 'text-slate-800' : 'text-slate-700 dark:text-slate-200'}`}>
                        {student.user_name}
                        {isCurrentStudent && <span className="ml-2 text-xs bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full">Você</span>}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-300">
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Nv. {student.level}
                        </span>
                        <span>•</span>
                        <span>{student.xp} AURA</span>
                        {student.win_streak > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-orange-500">🔥 x{student.win_streak}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mural de Gratidão - Support History */}
      {supportHistory.length > 0 && (
        <div className="container mx-auto px-4 pb-8" data-testid="gratitude-wall">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 shadow-lg border-2 border-indigo-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                <HandHeart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-outfit font-bold text-xl text-slate-800">Mural de Gratidao</h3>
                <p className="text-sm text-slate-500">{supportGroupName || 'Historico de ajudas do grupo'}</p>
              </div>
            </div>
            
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {supportHistory.map((entry) => {
                const isHP = entry.effect_type === 'hp';
                const isFromMe = entry.is_from_me;
                const isToMe = entry.is_to_me;
                const timeAgo = getTimeAgo(entry.created_at);
                
                return (
                  <div
                    key={entry.history_id}
                    data-testid={`support-entry-${entry.history_id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isToMe
                        ? 'bg-green-50 border border-green-200'
                        : isFromMe
                          ? 'bg-indigo-50 border border-indigo-200'
                          : 'bg-white/70 border border-slate-200'
                    }`}
                  >
                    {/* Effect icon */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isHP ? 'bg-rose-100' : 'bg-cyan-100'
                    }`}>
                      {isHP ? (
                        <Heart className="w-4 h-4 text-rose-500" />
                      ) : (
                        <Zap className="w-4 h-4 text-cyan-500" />
                      )}
                    </div>

                    {/* Message */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-nunito text-slate-800 leading-snug">
                        <span className="font-bold">{isFromMe ? 'Voce' : entry.from_name}</span>
                        {' usou '}
                        <span className="font-bold text-indigo-600">{entry.power_name}</span>
                        {' em '}
                        <span className="font-bold">{isToMe ? 'voce' : entry.to_name}</span>
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs font-bold ${isHP ? 'text-rose-600' : 'text-cyan-600'}`}>
                          +{entry.effect_value} {isHP ? 'HP' : 'Energia'}
                        </span>
                        <span className="text-[10px] text-slate-400">{timeAgo}</span>
                      </div>
                    </div>

                    {/* Badge */}
                    {isToMe && (
                      <span className="text-[10px] font-bold bg-green-200 text-green-800 px-2 py-0.5 rounded-full flex-shrink-0">
                        Recebido
                      </span>
                    )}
                    {isFromMe && (
                      <span className="text-[10px] font-bold bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full flex-shrink-0">
                        Enviado
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Duel Lobby Modal */}
      <Dialog open={duelLobbyOpen} onOpenChange={setDuelLobbyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-xl">
              <Swords className="w-6 h-6 text-red-500" />
              Arena de Duelo
            </DialogTitle>
            <DialogDescription>
              {selectedDuel?.title}
            </DialogDescription>
          </DialogHeader>

          {!lobbyMode ? (
            /* Mode Selection */
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 p-4 rounded-xl border-2 border-red-200">
                <h4 className="font-bold text-slate-800 mb-2">{selectedDuel?.title}</h4>
                <p className="text-sm text-slate-600 mb-2">{selectedDuel?.subject}</p>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                    {selectedDuel?.difficulty_level}
                  </span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    10 questões
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => handleCreateLobby(selectedDuel)}
                  className="flex flex-col items-center gap-2 h-auto py-6 bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  data-testid="create-lobby-btn"
                >
                  <Users className="w-8 h-8" />
                  <span className="font-bold">Criar Sala</span>
                  <span className="text-xs opacity-80">Gerar código</span>
                </Button>
                
                <Button
                  onClick={() => setLobbyMode('join')}
                  className="flex flex-col items-center gap-2 h-auto py-6 bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                  data-testid="join-lobby-btn"
                >
                  <ArrowRight className="w-8 h-8" />
                  <span className="font-bold">Entrar em Sala</span>
                  <span className="text-xs opacity-80">Digitar código</span>
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <div className="font-semibold text-amber-800 mb-1">⚔️ Regras do Duelo:</div>
                <ul className="text-amber-700 space-y-1 text-xs">
                  <li>• 5 corações cada • 45s por questão</li>
                  <li>• Vencedor: +200 AURA + Insígnia</li>
                  <li>• Perdedor: -20 HP</li>
                </ul>
              </div>
            </div>
          ) : lobbyMode === 'create' ? (
            /* Created Lobby - Waiting for opponent */
            <div className="space-y-4 text-center">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
                <div className="text-sm text-slate-600 mb-2">Compartilhe o código:</div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-4xl font-mono font-bold tracking-widest text-green-700 bg-white px-4 py-2 rounded-lg border-2 border-green-300">
                    {lobbyCode}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyLobbyCode}
                    className="text-green-600"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-slate-500">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Aguardando oponente...</span>
              </div>

              <Button
                onClick={handleStartDuel}
                disabled={!createdLobby}
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                data-testid="start-duel-btn"
              >
                <Swords className="w-4 h-4 mr-2" />
                Iniciar Duelo
              </Button>
            </div>
          ) : (
            /* Join Lobby - Enter code */
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-sm text-slate-600 mb-4">Digite o código da sala:</div>
                <Input
                  type="text"
                  maxLength={6}
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-3xl font-mono font-bold tracking-widest h-16"
                  data-testid="join-code-input"
                />
              </div>

              <Button
                onClick={handleJoinLobby}
                disabled={joinCode.length !== 6 || joiningLobby}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                data-testid="submit-join-btn"
              >
                {joiningLobby ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Entrar na Sala
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                onClick={() => setLobbyMode(null)}
                className="w-full"
              >
                Voltar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDashboard;
